import { Button, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaArrowsAltH, FaDownload, FaPlusCircle, FaExclamationTriangle, FaMarker } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import { isAuthenticated, hasRole } from '../services/KeycloakService';
import Config from '../config/Config';
import SectionWrapper from './SectionWrapper';
import '../css/DppActionList.css';

function DppActionList({
  onChangeAccessControl,
  onDownload,
  onSetReturnNumber,
  onAddCO2Consumption,
  onAddWaterMeterReadings,
  onReportProblem,
}) {
  const { mainColor, secondaryColor } = Config.appearance;
  const intl = useIntl();
  const sectionTitle = intl.formatMessage({ id: 'actions' });
  const labelAccessControl = intl.formatMessage({ id: 'action_access_control' });
  const labelDownload = intl.formatMessage({ id: 'action_download' });
  const labelAddCO2Consumption = intl.formatMessage({ id: 'action_add_co2_consumptions' });
  const labelAddWaterMeterReadings = intl.formatMessage({ id: 'action_add_watermeter_readings' });
  const labelSetReturnNumber = intl.formatMessage({ id: 'action_set_return_number' });
  const labelReportProblem = intl.formatMessage({ id: 'action_report_problem' });
  const noAccessMessage = intl.formatMessage({ id: 'no_access_message', defaultMessage: 'Not authorized for this action' });

  const items = [
    {
      label: labelAccessControl,
      icon: FaArrowsAltH,
      handler: onChangeAccessControl,
      roles: ['admin'],
      noAccessAction: 'disable'
    },
    {
      label: labelDownload,
      icon: FaDownload,
      handler: onDownload,
      roles: ['dpp-private-access', 'dpp-intermediate-access', 'dpp-public-access', 'admin'],
      noAccessAction: 'disable'
    },
    {
      label: labelSetReturnNumber,
      icon: FaMarker,
      handler: onSetReturnNumber,
      roles: ['dpp-private-access', 'dpp-intermediate-access', 'admin'],
      noAccessAction: 'disable'
    },
    {
      label: labelAddCO2Consumption,
      icon: FaPlusCircle,
      handler: onAddCO2Consumption,
      roles: ['dpp-private-access', 'dpp-intermediate-access', 'admin'],
      noAccessAction: 'disable'
    },
    {
      label: labelAddWaterMeterReadings,
      icon: FaPlusCircle,
      handler: onAddWaterMeterReadings,
      roles: ['dpp-private-access', 'dpp-intermediate-access', 'admin'],
      noAccessAction: 'disable'
    },
    {
      label: labelReportProblem,
      icon: FaExclamationTriangle,
      handler: onReportProblem,
      roles: ['dpp-private-access', 'dpp-intermediate-access', 'admin'],
      noAccessAction: 'disable'
    },
  ];

  // Function to check if the user has access to the button based on roles
  const hasAccess = (roles) => {
    if (!isAuthenticated()) return false; // Disable all buttons if not authenticated
    if (!roles || roles.length === 0) return true; // No roles specified means accessible to all authenticated users
    return roles.some(role => hasRole(role)); // User has at least one required role
  };

  return (
    <SectionWrapper
      title={sectionTitle}
      titleStyle={{ '--dpp-main-color': mainColor }}
      contentStyle={{ '--dpp-secondary-color': secondaryColor }}
    >
      <ButtonGroup vertical className="w-100">
        {items.map((it, idx) => {
          const Icon = it.icon;
          const canAccess = hasAccess(it.roles);

          // If user cannot access and noAccessAction is 'hide', skip rendering
          if (!canAccess && it.noAccessAction === 'hide') {
            return null;
          }

          // Render button, disabled if no access and noAccessAction is 'disable'
          const button = (
            <Button
              key={idx}
              className="action-list-item d-flex align-items-center"
              onClick={canAccess ? it.handler : undefined} // Disable click handler if no access
              disabled={!canAccess} // Disable button interaction
              style={{
                '--dpp-main-color': mainColor
              }}
            >
              <Icon className="action-list-icon" />
              <span>{it.label}</span>
            </Button>
          );

          // If disabled and noAccessAction is 'disable', wrap with a div and OverlayTrigger
          if (!canAccess && it.noAccessAction === 'disable') {
            return (
              <OverlayTrigger
                key={idx}
                placement="auto"
                overlay={<Tooltip id={`tooltip-${idx}`}>{noAccessMessage}</Tooltip>}
              >
                <div className="action-list-item action-list-item-disabled d-flex align-items-center">
                  <Icon className="action-list-icon" />
                  <span>{it.label}</span>
                </div>
              </OverlayTrigger>
            );
          }

          // Otherwise, return the button as is
          return button;
        })}
      </ButtonGroup>
    </SectionWrapper>
  );
}

export default DppActionList;